import { Fragment, useEffect, useCallback } from "react";
import {
  ScrollRestoration,
  useSearchParams,
  useNavigate,
} from "react-router-dom";
import { useSelector } from "react-redux";
import Home from "../publics/Home";
import ProfileV2 from "../private/common/ProfileV2";
import {
  validateReferralCode,
  calculateCookieLifetime,
  decodeReferralCode,
  getServiceRoute,
} from "../../utils/referral/referralHelpers";
import {
  setReferralCookie,
  setAccountCreationCookie,
} from "../../utils/cookies/cookieHelpers";
import { createReferralConversion } from "../../api/referral/referralAPI";
import { CONVERSION_STATUS } from "../../utils/constants/conversionStatuses";
import AppLoadingGhost from "../../components/ui/loading/skeletons/global/AppLoadingGhost";

function RootApp() {
  // Knox HTTP-only cookie is the source of truth
  // Use Redux state instead of loader
  const { isAuthenticated, isLoading } = useSelector((state) => state.auth);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const referralCode = searchParams.get("ref");
  const accountCreationRef = searchParams.get("ac-ref");

  const handleReferralLink = useCallback(
    async (refCode) => {
      // Check if user is authenticated
      if (isAuthenticated) {
        try {
          // Decode referral code to extract service information
          const decodedReferral = decodeReferralCode(refCode);

          if (!decodedReferral) {
            return;
          }

          const apiResponse = await createReferralConversion({
            referral_code: refCode,
            service: decodedReferral.svc,
            conversion_type: CONVERSION_STATUS.PENDING,
          });

          if (apiResponse.ok) {
            // Check if response has content before trying to parse JSON
            const contentType = apiResponse.headers.get("content-type");

            if (contentType && contentType.includes("application/json")) {
              try {
                await apiResponse.json();
              } catch (error) {
                // Handle error
              }
            }

            // Clean the URL parameter
            setSearchParams((params) => {
              params.delete("ref");
              return params;
            });

            // Redirect to the service route based on the referral code
            const serviceRoute = getServiceRoute(decodedReferral.svc);
            navigate(serviceRoute, { replace: true });
          } else {
            try {
              await apiResponse.json();
            } catch (error) {
              // Handle error
            }

            // Clean the URL parameter even if there was an error
            setSearchParams((params) => {
              params.delete("ref");
              return params;
            });

            // Redirect to profile on error
            navigate("/", { replace: true });
          }
        } catch (error) {
          // Clean the URL parameter
          setSearchParams((params) => {
            params.delete("ref");
            return params;
          });

          // Redirect to profile on error
          navigate("/", { replace: true });
        }
      } else {
        // User is NOT authenticated - handle guest referral logic
        // Validate referral code
        const validation = validateReferralCode(refCode);

        if (validation.isValid) {
          try {
            // Calculate cookie lifetime: min(7 days, token lifetime remaining)
            const cookieLifetime = calculateCookieLifetime(refCode);

            // Set referral cookie (will override if exists)
            setReferralCookie(refCode, cookieLifetime);

            // Clean the URL parameter
            setSearchParams((params) => {
              params.delete("ref");
              return params;
            });

            // Redirect to SignUp
            navigate("/sign-up", { replace: true });
          } catch (error) {
            // Handle error
          }
        } else {
          // Invalid referral code

          // Clean invalid parameter and continue normal flow
          setSearchParams((params) => {
            params.delete("ref");
            return params;
          });
        }
      }
    },
    [isAuthenticated, setSearchParams, navigate],
  );

  // Handle referral logic when component mounts
  useEffect(() => {
    if (isLoading) return;
    if (referralCode) {
      handleReferralLink(referralCode);
    }
  }, [referralCode, isAuthenticated, isLoading, handleReferralLink]);

  // Account Creation Referral
  useEffect(() => {
    if (!accountCreationRef) return;

    // Calculate cookie lifetime based on token expiration
    const cookieLifetime = calculateCookieLifetime(accountCreationRef);

    // Save cookie with dynamic lifetime
    setAccountCreationCookie(accountCreationRef, cookieLifetime);

    // Clean URL
    setSearchParams((params) => {
      params.delete("ac-ref");
      return params;
    });
  }, [accountCreationRef, setSearchParams]);

  /**
   * If when getting to root app,
   * 1) While loading, show loading placeholder (prevents flicker on page refresh)
   * 2) User is not authenticated, then is an anonymous user and will be routed to the public pages.
   * 3) User is authenticated, user will be routed to his profile
   * Knox HTTP-only cookie is the source of truth
   */
  return (
    <Fragment>
      <ScrollRestoration />
      {isLoading && <AppLoadingGhost />}
      {!isLoading && !isAuthenticated && <Home />}
      {!isLoading && isAuthenticated && <ProfileV2 />}
    </Fragment>
  );
}

export default RootApp;
